import path from "node:path";
import type { Response } from "@iflyrpa/share";
import { downloadImage, getFilenameFromUrl, success } from "@iflyrpa/share";

import { copyHtmlToClipboard, TimeFormatter } from "../../utils";
import type { PublishAction } from "./index";

export const rpaAction: PublishAction = async (task, params) => {
    const tmpCachePath = task.getTmpPath();

    // 过滤无效 cookie：
    // 历史上 baijiahaoLogin 的序列化 bug 会产出 name:"" value:"undefined" 的脏数据，
    // 这些 cookie 注入时会被 Electron reject，且可能顶替掉真正的鉴权 cookie（如 XFCS）。
    // 注入失败是静默的，因此在这里先剔除，避免登录态不完整导致误判“登录失效”。
    const validCookies = (params.cookies || []).filter((it) => it.name && it.value && it.value !== "undefined");

    const page = await task.createPage({
        show: task.debug,
        url: params.url || "https://baijiahao.baidu.com/builder/rc/edit?type=news&is_from_cms=1",
        cookies: validCookies,
    });

    // 增加页面默认超时时间，避免网络慢时加载超时
    page.setDefaultTimeout(60000); // 60秒
    page.setDefaultNavigationTimeout(60000); // 60秒

    // 0. 校验登录
    try {
        await page.waitForSelector(".bjh-edit-header", { state: "visible" });
    } catch (_error) {
        return { code: 414, message: "登录失效", data: page.url() };
    }

    // 1. 等待编辑器加载出来（线上网络慢时可能超时，先等 networkidle 再等编辑器）
    await page.waitForLoadState("domcontentloaded").catch(() => {});
    try {
        await page.waitForSelector(".edui-editor-toolbarbox", { state: "visible", timeout: 90000 });
    } catch (_err) {
        // 编辑器未加载出来，尝试刷新一次
        await page.reload({ waitUntil: "domcontentloaded", timeout: 60000 });
        // 刷新后重新校验登录
        const isLoggedIn = await page
            .locator(".bjh-edit-header")
            .isVisible({ timeout: 15000 })
            .catch(() => false);
        if (!isLoggedIn) {
            return { code: 414, message: "登录失效", data: page.url() };
        }
        await page.waitForSelector(".edui-editor-toolbarbox", { state: "visible", timeout: 90000 });
    }

    // 2. 关闭纯文本粘贴，默认开启纯文本粘贴的提示，先关闭
    if (await page.locator("#_onceTip-news_pasteplain_tip").isVisible()) {
        await page.locator("#_onceTip-news_pasteplain_tip .confirm").click();
    }

    // 2. 新增风险检测提示，先关闭
    if (await page.locator(".cheetah-tour .cheetah-tour-inner").filter({ hasText: "新增风险检测" }).isVisible()) {
        await page
            .locator(".cheetah-tour .cheetah-tour-inner")
            .filter({ hasText: "新增风险检测" })
            .locator(".cheetah-btn")
            .filter({ hasText: "我知道了" })
            .click();
    }
    // 2. 关闭纯文本粘贴
    // 有个默认开启纯文本粘贴的提示，先关闭
    if (await page.locator(".cheetah-tour .cheetah-tour-inner .cheetah-tour-close").isVisible()) {
        await page.locator(".cheetah-tour .cheetah-tour-inner .cheetah-tour-close").click();
    }

    // 判断是否开启纯文本粘贴，如果开启，点击关闭
    if (
        await page.locator(".edui-editor-toolbarbox .edui-button.edui-for-pasteplain .edui-state-checked").isVisible()
    ) {
        await page.locator(".edui-editor-toolbarbox .edui-button.edui-for-pasteplain .edui-state-checked").click();
    }

    // 3. 填写标题
    // 百家号使用 Lexical 编辑器，使用键盘逐字输入更可靠
    const titleEditor = page.locator(".input-box[data-testid='news-title-input'] div[contenteditable='true']");

    // 点击编辑器激活
    await titleEditor.click();

    // 等待编辑器获得焦点
    await page.waitForTimeout(200);

    // 先清空可能存在的内容
    await page.keyboard.press("Control+A");
    await page.keyboard.press("Delete");

    // 逐字输入标题（添加小延迟模拟真实输入）
    await page.keyboard.type(params.title, { delay: 50 });

    // 等待一下确保内容已设置
    await page.waitForTimeout(300);

    // 4. 填写正文
    await copyHtmlToClipboard(page, params.content);
    // 定位目标元素并聚焦
    await page.click("#ueditor .edui-editor-iframeholder iframe");
    // 模拟Ctrl+A全选
    await page.keyboard.press("Control+A");
    // 模拟Ctrl+V粘贴
    await page.keyboard.press("Control+V");

    // 等待 1 秒钟（让正文中的图片上传上去）
    await page.waitForTimeout(1000);

    // 5. 设置封面
    const coverFormItem = page.locator(".bjh-edit-wrap-set .cheetah-form-item").nth(0);
    const isSingleCover = params.settingInfo.baijiahaoCoverType === "single";
    if (isSingleCover) {
        // 单图
        await coverFormItem
            .locator(".cheetah-form-item-control label.cheetah-radio-wrapper")
            .filter({ hasText: "单图" })
            .click();
    } else {
        // 多图
        await coverFormItem
            .locator(".cheetah-form-item-control label.cheetah-radio-wrapper")
            .filter({ hasText: "三图" })
            .click();
    }

    // 获取待上传图片的本地路径
    const images = await Promise.all(
        (isSingleCover ? [params.settingInfo.baijiahaoSingleCover] : params.settingInfo.baijiahaoMultCover).map(
            (url) => {
                const fileName = getFilenameFromUrl(url);
                return downloadImage(url, path.join(tmpCachePath, fileName));
            },
        ),
    );

    // 点击第一个封面槽打开弹窗（单图/多图都只需打开一次弹窗）
    const firstSlot = page.locator(".cheetah-spin-container").first();
    await firstSlot.evaluate((node) => node.scrollIntoView());
    if (await firstSlot.locator(".actions-wrap").isVisible()) {
        await firstSlot.locator(".actions-wrap .action").filter({ hasText: "更换" }).click();
    } else {
        await firstSlot.click();
    }

    // 等待弹窗出现
    await page.waitForSelector(".cheetah-modal-root .cheetah-modal", { state: "visible" });

    // 切换到「正文/本地上传」
    const modal = page.locator(".cheetah-modal-root .cheetah-modal");
    const localTab = modal.locator(".cheetah-tabs-tab[data-node-key='local_main']");
    if (await localTab.isVisible()) {
        await localTab.click();
        await page.waitForTimeout(300);
    }

    // 上传按钮：含"本地上传"文字区域内
    const uploadBtn = modal
        .locator("div[class*='-upload']")
        .filter({ has: page.locator("div[class*='-text']", { hasText: "本地上传" }) })
        .locator("span.cheetah-upload");

    for (let i = 0; i < images.length; i++) {
        const image = images[i];
        // 记录上传前列表数量，用于等待新图片出现
        const beforeCount = await modal.locator("div[class*='-imgItem']").count();

        // waitForEvent 必须在 click 之前声明，两者并发执行
        const fileChooserPromise = page.waitForEvent("filechooser", { timeout: 15000 });
        await uploadBtn.click();
        const fileChooser = await fileChooserPromise;
        await fileChooser.setFiles([image]);

        // 等待新图片上传完成（列表数量增加）
        await page
            .waitForFunction(
                (before: number) => document.querySelectorAll("[class*='-imgItem']").length > before,
                beforeCount,
                { timeout: 15000 },
            )
            .catch(() => page.waitForTimeout(2000)); // 降级等待

        await page.waitForTimeout(300);

        // 刚上传的本地图片
        const allBlobItems = modal.locator("div[class*='-imgItem']").filter({ has: page.locator("img[src^='blob:']") });

        const count = await allBlobItems.count();
        let clicked = false;
        for (let idx = 0; idx < count; idx++) {
            const item = allBlobItems.nth(idx);
            const isSelected = await item
                .locator("div[class*='-checkbox']")
                .evaluate((el) => el.className.includes("selected"))
                .catch(() => false);
            if (!isSelected) {
                await item.locator("div[class*='-hotArea']").click();
                await page.waitForTimeout(300);
                clicked = true;
                break;
            }
        }
        if (!clicked) {
            // 降级：直接点最后一个 blob item（兜底，不应走到这里）
            await allBlobItems.last().locator("div[class*='-hotArea']").click();
            await page.waitForTimeout(300);
        }
    }

    // 点击确认按钮
    await modal.locator("button[class*='confirmBtn']").click();
    // 等待弹窗关闭
    await page.waitForSelector(".cheetah-modal-root .cheetah-modal", { state: "hidden", timeout: 10000 });
    await page.waitForTimeout(500);

    // 6. 摘要
    const abstractTextarea = page.locator(".bjh-edit-wrap-set textarea.cheetah-input").first();
    if (await abstractTextarea.isVisible({ timeout: 3000 }).catch(() => false)) {
        await abstractTextarea.click();
        await abstractTextarea.fill(params.settingInfo.baijiahaoAbstract);
    }

    // 7. 设置（页面升级后的新结构）

    // 关闭「公共政策和时事」浮层提示（如有）
    const policyTip = page.locator(
        '.cheetah-public .cheetah-popover-content .cheetah-popconfirm-message-icon span[class*="-icon"]',
    );
    if (await policyTip.isVisible({ timeout: 3000 }).catch(() => false)) {
        await policyTip.click();
        await page.waitForTimeout(200);
    }

    // ── 7a. 智能创作区域

    // 处理 baijiahaoPublishConf 配置项 自动生成播客
    if (params.settingInfo.baijiahaoPublishConf && params.settingInfo.baijiahaoPublishConf.length > 0) {
        const aiSettingSection = page.locator("div[class*='aiSetting']");
        for (const item of params.settingInfo.baijiahaoPublishConf) {
            const confCheckbox = aiSettingSection
                .locator("label.cheetah-checkbox-wrapper")
                .filter({ has: page.locator(`span.${item.confName}`) });

            if (await confCheckbox.isVisible()) {
                const isChecked = await confCheckbox.locator("input.cheetah-checkbox-input").isChecked();
                if (isChecked !== item.isChecked) {
                    await confCheckbox.click();
                    await page.waitForTimeout(200);
                }
            }
        }
    }

    // 处理"图文转动态"复选框
    const event2NewsCheckbox = page.locator("label.cheetah-checkbox-wrapper").filter({ hasText: "图文转动态" });
    if (await event2NewsCheckbox.isVisible()) {
        const isChecked = await event2NewsCheckbox.locator("input#event2news").isChecked();
        const shouldCheck = !!params.settingInfo.baijiahaoEvent2News;
        if (isChecked !== shouldCheck) {
            await event2NewsCheckbox.click();
            await page.waitForTimeout(200);
        }
    }

    // 7b. 创作声明区域
    const statementSection = page.locator("div[class*='statementSetting']");

    // 处理"采用AI生成内容"复选框
    const isAi = params.settingInfo.baijiahaoIsAi?.includes("isAi");
    const aigcCheckbox = statementSection
        .locator("label.cheetah-checkbox-wrapper")
        .filter({ has: page.locator("span.aigc_bjh_status") });
    if (await aigcCheckbox.isVisible()) {
        const isChecked = await aigcCheckbox.locator("input.cheetah-checkbox-input").isChecked();
        if (isChecked !== !!isAi) {
            await aigcCheckbox.click();
            await page.waitForTimeout(200);
        }
    }

    // 处理"来源说明"复选框，勾选后展开时间/地点选择器
    const hasEventSpec = !!(params.settingInfo.baijiahaoEventSpec?.time || params.settingInfo.baijiahaoEventSpec?.pos);
    const sourceCheckLabel = statementSection.locator("div[class*='sourceCheck'] label.cheetah-checkbox-wrapper");
    if (await sourceCheckLabel.isVisible()) {
        const isSourceChecked = await sourceCheckLabel.locator("input.cheetah-checkbox-input").isChecked();
        if (!isSourceChecked && hasEventSpec) {
            // 需要填写来源 → 勾选，展开时间/地点区域
            await sourceCheckLabel.click();
            await page.waitForTimeout(400);
        } else if (isSourceChecked && !hasEventSpec) {
            // 不需要来源 → 取消勾选
            await sourceCheckLabel.click();
            await page.waitForTimeout(300);
        }
    }

    // 填写事件时间（DatePicker：input[placeholder='请选择时间']，在 .a61da01300f09f44-sourceDatePicker 内）
    // 填写事件地点（Cascader：div[class*='cascader']，placeholder='请选择地点'，需点开下拉后按文字选择）
    if (hasEventSpec) {
        // 等待来源说明展开区域出现
        const sourceWrap = page.locator("div[class*='sourceWrap']");
        await sourceWrap.waitFor({ state: "visible", timeout: 5000 });

        // 事件时间：cheetah DatePicker
        // baijiahaoEventSpec.time 格式为 "YYYY-MM-DD"，对应日历 td[title='YYYY-MM-DD']
        if (params.settingInfo.baijiahaoEventSpec?.time) {
            const targetDate = params.settingInfo.baijiahaoEventSpec.time; // e.g. "2026-04-07"
            const datePickerInput = sourceWrap.locator(
                "div[class*='sourceDatePicker'] input[placeholder='请选择时间']",
            );
            if (await datePickerInput.isVisible()) {
                // 点击 input 打开日历面板
                await datePickerInput.click();
                await page.waitForTimeout(300);

                // 等待日历面板出现
                const pickerPanel = page.locator(".cheetah-picker-dropdown:not(.cheetah-picker-dropdown-hidden)");
                await pickerPanel.waitFor({ state: "visible", timeout: 5000 });

                // 解析目标年月，判断是否需要翻页
                const [targetYear, targetMonth] = targetDate.split("-").map(Number);

                // 最多翻 24 个月
                for (let attempt = 0; attempt < 24; attempt++) {
                    // 读取当前面板显示的年月
                    const yearText = await pickerPanel.locator(".cheetah-picker-year-btn").textContent();
                    const monthText = await pickerPanel.locator(".cheetah-picker-month-btn").textContent();
                    const currentYear = Number(yearText?.replace("年", ""));
                    const currentMonth = Number(monthText?.replace("月", ""));

                    if (currentYear === targetYear && currentMonth === targetMonth) break;

                    // 判断目标在当前之前还是之后
                    const isAfter =
                        targetYear > currentYear || (targetYear === currentYear && targetMonth > currentMonth);
                    if (isAfter) {
                        await pickerPanel.locator(".cheetah-picker-header-next-btn").click();
                    } else {
                        await pickerPanel.locator(".cheetah-picker-header-prev-btn").click();
                    }
                    await page.waitForTimeout(200);
                }

                // 点击目标日期（td[title='YYYY-MM-DD'] 精确匹配）
                await pickerPanel.locator(`td[title='${targetDate}'] .cheetah-picker-cell-inner`).click();
                await page.waitForTimeout(300);
            }
        }

        // 事件地点：Cascader 级联选择器，点击后从下拉菜单中选择匹配项
        if (params.settingInfo.baijiahaoEventSpec?.pos) {
            const cascader = sourceWrap.locator("div[class*='cascader']");
            if (await cascader.isVisible()) {
                // 点击打开级联下拉
                await cascader.click();
                await page.waitForTimeout(400);

                // pos 格式示例："北京市,海淀区" 或 "北京市"，逐级选择
                const levels = params.settingInfo.baijiahaoEventSpec.pos.split(",");
                for (const level of levels) {
                    const option = page
                        .locator(".cheetah-cascader-menus .cheetah-cascader-menu-item")
                        .filter({ hasText: level });
                    if ((await option.count()) > 0) {
                        await option.first().click();
                        await page.waitForTimeout(300);
                    }
                }

                // 按 Escape 关闭下拉（若未自动关闭）
                const cascaderDropdown = page.locator(".cheetah-cascader-menus");
                if (await cascaderDropdown.isVisible()) {
                    await page.keyboard.press("Escape");
                    await page.waitForTimeout(200);
                }
            }
        }
    }

    // 8. 活动投稿（label[for='task'] 定位 form-item，div[class*='taskName'] 定位卡片文字）
    if (params.settingInfo.baijiahaoActivity.length > 0 && params.settingInfo.baijiahaoSelectActivityCache.length > 0) {
        const activity = params.settingInfo.baijiahaoActivity.filter((it) =>
            params.settingInfo.baijiahaoSelectActivityCache.includes(it.id),
        );
        if (activity.length > 0) {
            const activityFormItem = page
                .locator(".bjh-edit-wrap-set")
                .locator(".cheetah-form-item")
                .filter({ has: page.locator("label[for='task']") });
            const name = activity[0].task_name;
            // 用 taskName 子元素匹配卡片文字，再点击所在的 taskCard
            const findCard = activityFormItem
                .locator("div[class*='taskCard']")
                .filter({ has: page.locator("div[class*='taskName']", { hasText: name }) });
            if ((await findCard.count()) > 0) {
                await findCard.first().click();
                await page.waitForTimeout(200);
            }
        }
    }

    // 9. 保存草稿 / 发布
    // biome-ignore-start lint/suspicious/noAsyncPromiseExecutor: 临时保留async
    const { articleId, message } = await new Promise<{ articleId: string; message: string }>(async (resolve) => {
        // 定义响应处理函数
        const handleResponse = async (response: Response) => {
            const url = response.url();
            if (url.includes("/pcui/article/save") || url.includes("/pcui/article/publish")) {
                const jsonResponse = await response.json();
                page.off("response", handleResponse);
                resolve({ articleId: jsonResponse?.ret?.article_id, message: jsonResponse?.errmsg });
            }
        };

        // 开始监听响应事件
        page.on("response", handleResponse);
        task._timerRecord.PrePublish = Date.now();

        const operatorContainer = page.locator(".editor-component-operator");
        if (params.saveType === "draft") {
            await operatorContainer
                .locator(".op-btn-outter-content")
                .filter({ hasText: "存草稿" })
                .locator("button")
                .click();
        } else {
            if (params.settingInfo.timer) {
                // 点击“定时发布”按钮
                await operatorContainer
                    .locator(".op-btn-outter-content")
                    .filter({ hasText: "定时发布" })
                    .locator("button")
                    .click();

                // 选择日期
                await page.locator(".timepublish-wrap-select .select-wrap", { hasText: "日" }).click();
                await page
                    .locator("#select-date_list + * .cheetah-select-item-option", {
                        hasText: TimeFormatter.format(params.settingInfo.timer, "M月dd日"),
                    })
                    .click();
                await page.waitForTimeout(200);

                // 选择小时
                await page.locator(".timepublish-wrap-select .select-wrap", { hasText: "点" }).click();
                await page.locator("#select-hour_list +div.rc-virtual-list").hover();

                const scrollHourInfo = await page.evaluate(() => {
                    const hourInner = document.querySelector("#select-hour_list + * .rc-virtual-list-holder-inner");
                    const hourElementHeight = hourInner?.children[0].clientHeight || 36;
                    const hourMapsElem = Array.from(
                        document.querySelectorAll(
                            "#select-hour_list + * .cheetah-select-item-option .cheetah-select-item-option-content",
                        ),
                    );
                    const hourMap = hourMapsElem.map((item) => item.textContent?.trim());

                    return { elementHeight: hourElementHeight, elementItem: hourMap };
                });
                const fistHourElem = scrollHourInfo.elementItem[0];
                const fistHourElemH = Number(fistHourElem?.split("点")[0]); // 列表渲染的第一个时间

                const lastHourElem = scrollHourInfo.elementItem[scrollHourInfo.elementItem.length - 1];
                const lastHourElemH = Number(lastHourElem?.split("点")[0]); // 列表渲染的最后一个时间

                const targetTimeH = Number(TimeFormatter.format(params.settingInfo.timer, "h")); // 目标时间
                await page.mouse.wheel(
                    0,
                    targetTimeH > lastHourElemH
                        ? (targetTimeH - lastHourElemH) * scrollHourInfo.elementHeight
                        : targetTimeH < fistHourElemH
                          ? -(fistHourElemH - targetTimeH) * scrollHourInfo.elementHeight
                          : 0,
                );
                await page
                    .locator("#select-hour_list + * .cheetah-select-item-option ", { hasText: `${targetTimeH}点` })
                    .click();
                await page.waitForTimeout(200);

                // 选择分钟
                await page.locator(".timepublish-wrap-select .select-wrap", { hasText: "分" }).click();
                await page.locator("#select-minute_list +div.rc-virtual-list").hover();

                const scrollMinuteInfo = await page.evaluate(() => {
                    const hourInner = document.querySelector("#select-minute_list + * .rc-virtual-list-holder-inner");
                    const hourElementHeight = hourInner?.children[0].clientHeight || 36;
                    const hourMapsElem = Array.from(
                        document.querySelectorAll(
                            "#select-minute_list + * .cheetah-select-item-option .cheetah-select-item-option-content",
                        ),
                    );
                    const hourMap = hourMapsElem.map((item) => item.textContent?.trim());

                    return { elementHeight: hourElementHeight, elementItem: hourMap };
                });
                const fistMinuteElem = scrollMinuteInfo.elementItem[0];
                const fistMinuteElemH = Number(fistMinuteElem?.split("分")[0]); // 列表渲染的第一个时间

                const lastMinuteElem = scrollMinuteInfo.elementItem[scrollMinuteInfo.elementItem.length - 1];
                const lastMinuteElemH = Number(lastMinuteElem?.split("分")[0]); // 列表渲染的最后一个时间

                const targetTimeM = Number(TimeFormatter.format(params.settingInfo.timer, "m")); // 目标时间
                await page.mouse.wheel(
                    0,
                    targetTimeM > lastMinuteElemH
                        ? (targetTimeM - lastMinuteElemH) * scrollMinuteInfo.elementHeight
                        : targetTimeM < fistMinuteElemH
                          ? -(fistMinuteElemH - targetTimeM) * scrollMinuteInfo.elementHeight
                          : 0,
                );
                await page
                    .locator("#select-minute_list + * .cheetah-select-item-option ", { hasText: `${targetTimeM}分` })
                    .click();
                await page.waitForTimeout(200);

                await page.locator(".cheetah-modal-confirm-btns button").filter({ hasText: "定时发布" }).click();
            } else {
                await operatorContainer
                    .locator(".op-btn-outter-content")
                    .filter({ hasText: /^发布/ })
                    .locator("button")
                    .click();
            }
        }
    });

    // 关闭页面
    await page.close();

    return success(articleId, articleId ? "发布成功" : message);
};
